import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

import { HelpTip } from './FieldLabel';
import { SkeletonRows } from './Skeleton';

export interface Column<T> {
  key: string;
  header: string;
  /** Alinhamento a direita para numeros, que se comparam pela ultima casa. */
  align?: 'left' | 'right' | undefined;
  /** Colunas secundarias somem em telas estreitas em vez de espremer o conteudo. */
  hideBelow?: 'sm' | 'md' | 'lg' | undefined;
  /** Ajuda (i) ao lado do titulo da coluna, com o mesmo botao do campo. */
  help?: string | undefined;
  /** Segundo titulo, da coluna que mostra dois dados na mesma celula. */
  detail?: string | undefined;
  /**
   * Largura da coluna na tabela fluida, como `9rem` ou `14%`. A coluna sem largura fica
   * com o que sobrar.
   */
  width?: string | undefined;
  render: (item: T) => ReactNode;
}

/**
 * Tabela de dados com os tres estados que toda listagem tem: carregando, vazia e
 * preenchida.
 *
 * Concentrar os tres aqui evita que cada modulo trate apenas o terceiro, que e o
 * unico visivel durante o desenvolvimento com dados prontos.
 *
 * Por padrao o navegador distribui as colunas pelo conteudo. Nessa conta, texto sem
 * quebra vale a largura inteira dele: o `truncate` nao corta nada, alarga a tabela e
 * espreme o resto das colunas no minimo, palavra por linha. A tabela `fluida` inverte a
 * conta: as colunas tem largura definida (`width`), a tabela nunca passa da largura que
 * recebeu e as linhas tem a mesma altura. Quem liga a tabela fluida passa a responder
 * por caber: o conteudo da celula precisa ser cortado em uma ou duas linhas, e a escolha
 * das colunas vem da largura medida, nao de `hideBelow`, que deixaria a largura da
 * coluna escondida ocupando a tabela.
 */
export function DataTable<T>({
  columns,
  items,
  keyOf,
  loading,
  empty,
  onRowClick,
  fluida = false,
}: {
  columns: Column<T>[];
  items: T[];
  keyOf: (item: T) => string;
  loading?: boolean | undefined;
  empty?: ReactNode | undefined;
  onRowClick?: ((item: T) => void) | undefined;
  fluida?: boolean | undefined;
}) {
  if (loading) {
    return <SkeletonRows rows={6} columns={Math.min(columns.length, 5)} />;
  }

  if (items.length === 0) {
    return <>{empty}</>;
  }

  return (
    <div className="rolagem-fina overflow-x-auto">
      <table
        className={cn(
          'w-full border-collapse text-sm',
          fluida ? 'table-fixed' : 'min-w-[40rem]',
        )}
      >
        {fluida ? (
          <colgroup>
            {columns.map((column) => (
              <col
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
              />
            ))}
          </colgroup>
        ) : null}
        <thead>
          <tr className="border-b border-line bg-surface-sunken">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  'text-2xs font-semibold uppercase tracking-wide text-ink-500',
                  fluida ? 'px-3 py-2 first:pl-5 last:pr-5' : 'px-5 py-2.5',
                  column.align === 'right' ? 'text-right' : 'text-left',
                  hiddenClass(column.hideBelow),
                )}
              >
                {column.help ? (
                  <HelpTip label={column.header} text={column.help} />
                ) : (
                  column.header
                )}
                {column.detail ? (
                  <>
                    {' '}
                    <span
                      title={column.detail}
                      className="block truncate font-normal tracking-normal text-ink-400 normal-case"
                    >
                      {column.detail}
                    </span>
                  </>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((item) => (
            <tr
              key={keyOf(item)}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
              className={cn(
                'transition-colors duration-(--duration-fast)',
                onRowClick ? 'cursor-pointer hover:bg-brand-50/40' : null,
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    'text-ink-700',
                    fluida
                      ? 'h-14 overflow-hidden px-3 py-1.5 first:pl-5 last:pr-5'
                      : 'px-5 py-3.5',
                    column.align === 'right' ? 'text-right tabular-nums' : 'text-left',
                    hiddenClass(column.hideBelow),
                  )}
                >
                  {column.render(item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function hiddenClass(breakpoint?: 'sm' | 'md' | 'lg'): string | null {
  if (!breakpoint) return null;
  if (breakpoint === 'sm') return 'hidden sm:table-cell';
  if (breakpoint === 'md') return 'hidden md:table-cell';
  return 'hidden lg:table-cell';
}
